/**
 * Métricas de capacidade e eficiência — as que decidem a compra.
 *
 * Latência por requisição diz se **um** usuário fica satisfeito. Estas dizem
 * quantos usuários cabem, a que custo por token e a que custo de energia — e é
 * isso que separa dois candidatos com o mesmo desempenho individual.
 *
 * O cálculo vive aqui, isolado, porque no Epic 3 ele migra para a camada de
 * domínio praticamente como está: são funções puras sobre números medidos.
 */

import {
  GPU_BANDWIDTH_TBS,
  MTP_ACCEPT_RATE,
  KV_BYTES_PER_TOKEN,
  MODEL_WEIGHTS_MB,
  RECURRENT_STATE_MB,
  TARGET_TOKENS_PER_SEC,
} from "./scale.js";

/** Segundos numa hora, usado na conversão de custo. */
const SECONDS_PER_HOUR = 3_600;

/** Fração da memória reservada ao motor, fora dos pesos e do cache de estado. */
const ENGINE_OVERHEAD_PCT = 0.08;

/**
 * Vazão agregada do sistema, em tokens por segundo.
 *
 * É a métrica de **capacidade**: quantos tokens o servidor inteiro entrega. Não
 * confundir com a vazão por requisição, que é a de latência — um servidor pode
 * ter vazão agregada alta e latência individual ruim, e a decisão de compra
 * precisa dos dois números.
 */
export function aggregateTokensPerSec(concurrency: number, tpotMs: number): number {
  if (tpotMs <= 0) {
    return 0;
  }
  return concurrency * (1_000 / tpotMs);
}

/** Requisições concluídas por segundo, dada a vazão agregada e o tamanho da resposta. */
export function requestsPerSec(aggregateTps: number, avgTokensOut: number): number {
  if (avgTokensOut <= 0) {
    return 0;
  }
  return aggregateTps / avgTokensOut;
}

/**
 * Tokens de contexto que cabem no cache de estado.
 *
 * É o teto de `concorrência × comprimento de contexto` que o hardware suporta.
 * Determina quantas sessões simultâneas cabem antes de o motor recusar ou
 * começar a despejar cache — limite que aparece como queda abrupta de vazão, e
 * não como degradação suave.
 */
export function kvCapacityTokens(
  gpuMemoryGbTotal: number,
  concurrency: number,
): number {
  const totalMb = gpuMemoryGbTotal * 1024;
  const availableMb =
    totalMb * (1 - ENGINE_OVERHEAD_PCT) - MODEL_WEIGHTS_MB - RECURRENT_STATE_MB * concurrency;
  if (availableMb <= 0) {
    return 0;
  }
  return Math.floor((availableMb * 1024 * 1024) / KV_BYTES_PER_TOKEN);
}

/**
 * Eficiência energética, em tokens por watt.
 *
 * Separa candidatos que empatam em desempenho. Ao longo de anos de operação, a
 * diferença de energia entre duas configurações pode superar a diferença de
 * preço de compra — e essa comparação não aparece em nenhum catálogo.
 */
export function tokensPerWatt(aggregateTps: number, powerWTotal: number): number {
  if (powerWTotal <= 0) {
    return 0;
  }
  return aggregateTps / powerWTotal;
}

/**
 * Custo por milhão de tokens, em dólares.
 *
 * É a métrica de comparação universal: normaliza hardware de portes e preços
 * diferentes numa unidade só, e é a única que permite comparar diretamente
 * comprar contra alugar.
 */
export function costPerMillionTokens(costPerHourUsd: number, aggregateTps: number): number {
  if (aggregateTps <= 0) {
    return 0;
  }
  const tokensPerHour = aggregateTps * SECONDS_PER_HOUR;
  return (costPerHourUsd / tokensPerHour) * 1_000_000;
}

/**
 * Bytes lidos a cada passo de decodificação.
 *
 * O decode lê os pesos do modelo somados ao estado de **todas** as requisições
 * ativas. É esta soma que impõe a banda de memória necessária — e é por isso
 * que concorrência alta em contexto longo esbarra em banda antes de esbarrar em
 * capacidade de cálculo.
 */
export function bytesPerStep(contextLen: number, concurrency: number): number {
  const weightsBytes = MODEL_WEIGHTS_MB * 1024 * 1024;
  const kvBytes = KV_BYTES_PER_TOKEN * contextLen * concurrency;
  const stateBytes = RECURRENT_STATE_MB * 1024 * 1024 * concurrency;
  return weightsBytes + kvBytes + stateBytes;
}

/**
 * Tokens entregues por passo de decodificação, com decodificação especulativa.
 *
 * 🛑 **Ignorar isto superestima a banda exercida.** Sem especulação, um passo
 * entrega um token; com ela, o modelo propõe `draftTokens` adiantados e aceita
 * cada um com probabilidade `acceptRate`, de modo que um único passo — e uma
 * única leitura dos pesos e do estado — entrega vários tokens.
 *
 * A esperança da série geométrica truncada dá o número médio de tokens por
 * passo. Tratar cada token como um passo produziu, na primeira versão desta
 * tela, banda exercida de 6,73 TB/s num hardware de 3,46 TB/s nominais —
 * impossível, e mascarado por uma saturação em 100%.
 */
export function tokensPerStep(draftTokens: number, acceptRate = MTP_ACCEPT_RATE): number {
  if (draftTokens <= 0 || acceptRate <= 0) {
    return 1;
  }
  if (acceptRate >= 1) {
    return draftTokens + 1;
  }
  return (1 - Math.pow(acceptRate, draftTokens + 1)) / (1 - acceptRate);
}

/**
 * Banda efetivamente exercida, em terabytes por segundo.
 *
 * Deriva de bytes por passo multiplicados por passos por segundo — e passos não
 * é o mesmo que tokens quando há decodificação especulativa.
 */
export function effectiveBandwidthTbs(
  contextLen: number,
  concurrency: number,
  tpotMs: number,
  draftTokens = 0,
): number {
  if (tpotMs <= 0) {
    return 0;
  }
  const tokensPerSecond = 1_000 / tpotMs;
  const stepsPerSec = tokensPerSecond / tokensPerStep(draftTokens);
  return (bytesPerStep(contextLen, concurrency) * stepsPerSec) / 1e12;
}

/**
 * Fração da banda nominal que o motor consegue exercer.
 *
 * É o número que sustenta toda extrapolação para outro hardware: com ele, a
 * previsão deixa de ser chute e passa a ser a banda nominal do candidato
 * multiplicada por uma eficiência **medida**.
 */
export function bandwidthEfficiencyPct(
  effectiveTbs: number,
  gpuModel: string,
  gpuCount: number,
): number {
  const nominal = (GPU_BANDWIDTH_TBS[gpuModel] ?? 0.864) * gpuCount;
  if (nominal <= 0) {
    return 0;
  }
  // Deliberadamente SEM saturar em 100: valor acima disso é fisicamente
  // impossível e denuncia erro no modelo de bytes por passo. Esconder atrás de
  // um teto transformaria o defeito em número plausível — foi o que aconteceu
  // na primeira versão desta tela.
  return (effectiveTbs / nominal) * 100;
}

/**
 * Concorrência máxima que ainda sustenta o alvo.
 *
 * Deriva da banda disponível: à medida que a concorrência sobe, o estado lido
 * por passo cresce, e a vazão por requisição cai. O ponto de cruzamento com o
 * alvo é o número que responde "quantos usuários simultâneos este hardware
 * atende" — que é a pergunta de dimensionamento.
 */
export function maxConcurrencyAtTarget(
  contextLen: number,
  gpuModel: string,
  gpuCount: number,
  measuredEfficiencyPct: number,
  draftTokens = 0,
): number {
  const nominalTbs = (GPU_BANDWIDTH_TBS[gpuModel] ?? 0.864) * gpuCount;
  const usableBytesPerSec = nominalTbs * 1e12 * (measuredEfficiencyPct / 100);
  // Passos necessários, não tokens: a especulação entrega vários tokens por passo.
  const stepsPerSecNeeded = TARGET_TOKENS_PER_SEC / tokensPerStep(draftTokens);

  // Busca o maior número de sessões cujo custo por passo ainda cabe na banda.
  // A relação não é linear — os pesos são fixos e o estado cresce com a
  // concorrência —, então resolver por varredura é mais claro que inverter.
  let best = 0;
  for (let concurrency = 1; concurrency <= 512; concurrency += 1) {
    const needed = bytesPerStep(contextLen, concurrency) * stepsPerSecNeeded;
    if (needed > usableBytesPerSec) {
      break;
    }
    best = concurrency;
  }
  return best;
}
